import React, { useState } from "react";
import Collapse from "../../../components/Collapse";
import { formatBook } from "../../../utils/formatOdds";

// Reference v-kvalmin / v-kvalmax: 1000 → "1K", 100000 → "1L"
function formatRangeVal(val) {
  const n = Number(val);
  if (val == null || val === "" || Number.isNaN(n)) return "";
  if (n >= 100000) return `${parseFloat((n / 100000).toFixed(2))}L`;
  if (n >= 1000) return `${parseFloat((n / 1000).toFixed(2))}K`;
  return String(parseFloat(n.toFixed(2)));
}

const isOpen = (m) => String(m?.gstatus || "").toUpperCase() === "OPEN";

// Reference orderBy(filter(sub, {subtype: "poker20"}), "sr")
const bySr = (list) => [...list].sort((a, b) => Number(a?.sr) - Number(b?.sr));

const ROWS = [
  ["Winner", "One Pair", "Two Pair"],
  ["Three of a Kind", "Straight", "Flush"],
  ["Full House", "Four of a Kind", "Straight Flush"],
];

/**
 * 20-20 Poker (poker20) bet table in the reference admin's markup (rendered inside `.casino-detail`):
 *   .poker20casino-container.d-none-small > .poker20left (Player A: main[0,2,..16]) + .poker20center
 *   + .poker20right (Player B: main[1,3,..17]); each block of 3 markets = title row
 *   (.casino-bl-box.casino-bl-box-title: name + range (i)) and an odds row (.back.casino-bl-box-item + book).
 * Styled by the ported reference rules in styles/index.css (.poker20 ...).
 */
export function BetTablePoker20({ data = [], onBetClick, exposures = {} }) {
  const [openRange, setOpenRange] = useState({});
  const toggle = (id) => setOpenRange((s) => ({ ...s, [id]: !s[id] }));

  const sub = Array.isArray(data) ? data : [];
  const poker20 = sub.filter((m) => m?.subtype === "poker20");
  const main = bySr(poker20.length ? poker20 : sub);

  // Both players' feed rows share the names ("Winner", "One Pair"); bets are stored with the
  // player: Winner = "Player A", the hands "One Pair A" (settlement reads these).
  const exposureOf = (m, player) => {
    if (!m?.nat) return 0;
    const name = m.nat === "Winner" ? `Player ${player}` : `${m.nat} ${player}`;
    const v = exposures?.[name] ?? exposures?.[String(name).toLowerCase()];
    return v != null ? Number(v) || 0 : 0;
  };

  const side = (offset, rangeBase, player) => (
    <div className={offset === 0 ? "poker20left" : "poker20right"}>
      {ROWS.map((names, r) => {
        const idx = [0, 1, 2].map((c) => (r * 3 + c) * 2 + offset);
        return (
          <React.Fragment key={r}>
            {r > 0 && " "}
            <div className={r > 0 ? "mt-1" : undefined}>
              <div className="casino-bl-box casino-bl-box-title">
                {names.map((name, c) => {
                  const id = `range${rangeBase + r * 3 + c}`;
                  const m = main[idx[c]];
                  return (
                    <React.Fragment key={name}>
                      {c > 0 && " "}
                      <div className="casino-bl-box-item">
                        <span>{name}</span>{" "}
                        <div className="ml-1 float-right">
                          <i
                            data-toggle="collapse"
                            data-target={`#${id}`}
                            className="fas fa-info-circle float-right"
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              toggle(id);
                            }}
                          />{" "}
                          <Collapse id={id} open={!!openRange[id]} className="icon-range">
                            {"\n                                                    R:"}
                            <span>{formatRangeVal(m?.min)}</span>-<span>{formatRangeVal(m?.max)}</span>
                          </Collapse>
                        </div>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>{" "}
              <div className="casino-bl-box">
                {idx.map((i, c) => {
                  const m = main[i];
                  const open = isOpen(m);
                  const exp = exposureOf(m, player);
                  return (
                    <React.Fragment key={i}>
                      {c > 0 && " "}
                      <div
                        className={`back casino-bl-box-item${open ? "" : exp ? " suspended lock-top" : " suspended"}`}
                        onClick={() => {
                          if (m && open && m.b) onBetClick?.(m.b, m.nat, m, player);
                        }}
                      >
                        <span className="casino-box-odd">{m?.b ?? 0}</span>{" "}
                        <span className={`casino-book ${exp > 0 ? "book-green" : "book-red"}`}>{formatBook(exp)}</span>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            </div>
          </React.Fragment>
        );
      })}
    </div>
  );

  return (
    <div className="poker20casino-container d-none-small">
      {side(0, 1, "A")} <div className="poker20center" /> {side(1, 10, "B")}
    </div>
  );
}

export default BetTablePoker20;
